import { FC, useRef, useEffect, useMemo, useCallback, useState } from "react";
import {
  transformDependentFieldsState,
  useField,
  UseFieldHookProps,
} from "packages/form";
// import { KeyboardDatePickerProps } from "@material-ui/pickers";
import { DatePickerProps } from "@mui/lab/DatePicker";
import { KeyboardDatePicker } from "components/styledComponent/datetime";
import Grid, { GridProps } from "@mui/material/Grid";
import { Omit, Merge } from "../types";
import Button from "@mui/material/Button";
//import { theme2 } from "app/audit/theme";
import "./style.css";
import { TextField } from "components/styledComponent/textfield";
import {
  getParsedDate,
  isValidDate,
} from "components/utils/utilFunctions/function";
import { useTranslation } from "react-i18next";
import { greaterThanDate, lessThanDate } from "registry/rulesEngine";
import { usePropertiesConfigContext } from "components/context/propertiesConfig/customProperties";
import { CircularProgress, InputAdornment } from "@mui/material";
import { isValid } from "date-fns";
import { useAuthContext } from "components/context/authContext/authContext";
//const themeObj: any = unstable_createMuiStrictModeTheme(theme2);
type KeyboardDatePickerPropsSubset = Omit<
  DatePickerProps<any>,
  "onChange" | "value"
>;

interface MyGridExtendedProps {
  GridProps?: GridProps;
  disableTimestamp?: boolean;
  enableGrid: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  isWorkingDate?: boolean;
  isMaxWorkingDate?: boolean;
  isMinWorkingDate?: boolean;
  ignoreInSubmit?: (
    dependentValues: any,
    object: { isSubmitting?: boolean; value?: string },
  ) => boolean | boolean;
  // ignoreInSubmit?: boolean;
  setFieldLabel?: (
    dependentFields?: any,
    value?: any,
  ) => { label: string; placeholder?: string } | null | undefined;
}

export type MyDataPickerAllProps = Merge<
  Merge<KeyboardDatePickerPropsSubset, MyGridExtendedProps>,
  UseFieldHookProps
>;
export const MyDatePicker: FC<MyDataPickerAllProps> = ({
  name: fieldName,
  validate,
  validationRun,
  shouldExclude,
  isReadOnly,
  postValidationSetCrossFieldValues,
  setValueOnDependentFieldsChange,
  runPostValidationHookAlways,
  dependentFields,
  fieldKey: fieldID,
  type,
  GridProps,
  enableGrid,
  //@ts-ignore
  isFieldFocused,
  InputProps,
  inputProps,
  runValidationOnDependentFieldsChange,
  skipValueUpdateFromCrossFieldWhenReadOnly,
  required,
  disablePast,
  disableFuture,
  ignoreInSubmit = false,
  label,
  setFieldLabel,
  placeholder,
  format,
  CircularProgressProps,
  componentType,
  ...others
}) => {
  const [selectedSection, setSelectedSection] = useState<any>(null);
  const { commonDateFormat } = usePropertiesConfigContext();
  const { authState } = useAuthContext();
  const {
    value,
    error,
    touched,
    handleChange,
    handleBlur,
    isSubmitting,
    fieldKey,
    name,
    excluded,
    readOnly,
    setIgnoreInSubmit,
    incomingMessage,
    whenToRunValidation,
    runValidation,
    dependentValues,
    validationRunning,
    setErrorAsCB,
  } = useField({
    name: fieldName,
    fieldKey: fieldID,
    dependentFields,
    validate,
    componentType,
    //uncomment this line if there is any issue while validating
    validationRun, // uncommented
    /**
     * By Altaf Shaikh on 19-Jan-2024
     * keep validationRun coming from metadata for new datepicker to work on blur event
     * Reason: in onblur event validation function not call due to this comment `validationRun: "onChange"` and uncomment `validationRun`.
     */
    // validationRun: "onChange",     // commented
    runPostValidationHookAlways,
    postValidationSetCrossFieldValues,
    isReadOnly,
    shouldExclude,
    runValidationOnDependentFieldsChange,
    skipValueUpdateFromCrossFieldWhenReadOnly,
    //@ts-ignore
  });

  // below code added for run validation if max or min date specified in metadata
  useEffect(() => {
    if (greaterThanDate(value, others?.maxDate)) {
      runValidation({ value, _maxDt: others?.maxDate });
    }
    if (lessThanDate(value, others?.minDate)) {
      runValidation({ value, _minDt: others?.minDate });
    }
  }, [value]);

  useEffect(() => {
    if (Boolean(ignoreInSubmit)) {
      if (typeof ignoreInSubmit === "function") {
        let result = ignoreInSubmit(
          transformDependentFieldsState(dependentValues),
          { isSubmitting, value },
        );
        setIgnoreInSubmit(result);
      } else {
        setIgnoreInSubmit(ignoreInSubmit);
      }
    }
  }, [ignoreInSubmit, setIgnoreInSubmit, dependentValues]);
  // useEffect(() => {
  //   setIgnoreInSubmit(ignoreInSubmit);
  // }, [ignoreInSubmit]);

  useEffect(() => {
    if (typeof value === "string") {
      let result: any = getParsedDate(value);
      //@ts-ignore
      if (!isNaN(result)) {
        handleChange(result);
      }
    }
  }, [value, handleChange]);
  const { t } = useTranslation();
  const focusRef = useRef<any>();
  const getFocus = () => {
    setTimeout(() => {
      focusRef?.current?.focus?.();
    }, 50);
  };
  useEffect(() => {
    if (isFieldFocused) {
      //@ts-ignore
      setTimeout(() => {
        //@ts-ignore
        focusRef?.current?.focus?.();
      }, 1);
    }
  }, [isFieldFocused]);

  // cbs update for set value through post validation
  useEffect(() => {
    if (incomingMessage !== null && typeof incomingMessage === "object") {
      const { isFieldFocused, value, ignoreUpdate } = incomingMessage;
      if (isFieldFocused) {
        getFocus();
      }
      if (Boolean(value) || value === "") {
        handleChange(value);
        if (!ignoreUpdate && whenToRunValidation === "onBlur") {
          runValidation({ value: value }, true);
        }
      }
    }
  }, [incomingMessage, handleChange, runValidation, whenToRunValidation]);

  useEffect(() => {
    if (typeof setValueOnDependentFieldsChange === "function") {
      let result = setValueOnDependentFieldsChange(
        transformDependentFieldsState(dependentValues),
        { value, isSubmitting },
      );
      if (result !== undefined && result !== null) {
        handleChange(result);
      }
    }
  }, [dependentValues, handleChange, setValueOnDependentFieldsChange]);

  const updatedLabel = useMemo(() => {
    if (typeof setFieldLabel === "function")
      return setFieldLabel(
        transformDependentFieldsState(dependentValues),
        value,
      );
  }, [setFieldLabel, dependentValues, value]);

  const isError = touched && (error ?? "") !== "";

  /** Functionality for CBS: To set future or past date based on working date on arrow up / down key press when field is empty.
   * And to move cusror to start or end on pressing home / end key.
   */
  const handleKeyDown = (e) => {
    const localFormat = format ?? commonDateFormat;
    if (
      authState?.workingDate &&
      (e.key === "ArrowUp" || e.key === "ArrowDown")
    ) {
      const date = e.target.value;
      // condition to check if field is empty with format checking
      if (
        !isValid(value) &&
        date?.toLowerCase() === localFormat?.toLowerCase()
      ) {
        const workingDate = authState?.workingDate;
        let currDate: number | string = "";
        if (e.key === "ArrowUp") {
          // next date from working date if pressed arrow up
          currDate = new Date().setDate(new Date(workingDate).getDate() + 1);
        } else if (e.key === "ArrowDown") {
          // previous date from working date if pressed arrow down
          currDate = new Date().setDate(new Date(workingDate).getDate() - 1);
        }

        setTimeout(() => handleChange(new Date(currDate)), 0);
      }
    } else if (e.key === "Home") {
      // set selection to first section
      setSelectedSection(0);
    } else if (e.key === "End") {
      // set selection to last section
      const delimiter = localFormat.includes("/")
        ? "/"
        : localFormat.includes("-")
          ? "-"
          : null;
      const sectionCount = delimiter ? localFormat.split(delimiter).length : 1;
      setSelectedSection(sectionCount - 1);
    }
  };

  if (excluded) {
    return null;
  }
  const result = (
    // <ThemeProvider theme={themeObj}>
    <KeyboardDatePicker
      {...others}
      key={fieldKey}
      label={updatedLabel?.label ? t(updatedLabel.label) : label}
      format={(format ?? commonDateFormat) || "dd/MM/yyyy"}
      value={value === "" || value === null ? null : getParsedDate(value)} //make sure to pass null when input is empty string
      //@ts-ignore
      tabIndex={readOnly ? -1 : undefined}
      onChange={(date: any) => handleChange(date)}
      onSelectedSectionsChange={(section) => {
        setSelectedSection(section);
      }}
      selectedSections={selectedSection}
      // allowKeyboardControl={true}
      //option 2 if validationRun: "onChange" is not set, uncomment this code
      // onClose={() => {
      //   setTimeout(() => {
      //     //@ts-ignore
      //     focusRef?.current?.focus?.();
      //   }, 1);
      // }}
      // FIX: on date/time change through popup, run handleBlur to validate
      onClose={() => {
        setTimeout(() => handleBlur(), 0);
      }}
      disabled={isSubmitting}
      readOnly={readOnly}
      disablePast={disablePast}
      disableFuture={disableFuture}
      slots={{
        textField: TextField,
      }}
      slotProps={{
        textField: {
          onBlur: handleBlur,
          onKeyDown: handleKeyDown,
          id: fieldKey,
          name,
          placeholder: updatedLabel?.placeholder
            ? t(updatedLabel.placeholder)
            : placeholder,
          autoComplete: "off",
          required: required,
          error: !isSubmitting && isError,
          helperText: !isSubmitting && isError ? error : null,
          inputProps: {
            tabIndex: readOnly ? -1 : undefined,
            ...inputProps,
          },
          InputProps: {
            style: { background: Boolean(readOnly) ? "#f3f3f3" : "" },
            readOnly: readOnly,
            startAdornment: validationRunning ? (
              <InputAdornment
                position="end"
                sx={{ position: "absolute", right: "38px" }}
              >
                <CircularProgress
                  color="secondary"
                  variant="indeterminate"
                  size={20}
                  {...CircularProgressProps}
                />
              </InputAdornment>
            ) : null,
            ...InputProps,
          },
          InputLabelProps: { shrink: true },
          inputRef: focusRef,
        },
        actionBar: {
          actions: ["today", "accept", "cancel"],
        },
      }}
      sx={{ width: "100%" }}
      cancelLabel={
        <Button
          color="secondary"
          style={{ border: "1px solid rgba(128, 0, 0, 0.23)" }}
          disableElevation
        >
          {t("Cancel")}
        </Button>
      }
      okLabel={
        <Button color="secondary" variant="contained" disableElevation>
          {t("Ok")}
        </Button>
      }
    />
    //</ThemeProvider>
  );
  if (Boolean(enableGrid)) {
    return (
      <Grid {...GridProps} key={fieldKey}>
        {result}
      </Grid>
    );
  } else {
    return result;
  }
};

export default MyDatePicker;
